import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, RefreshCw, Eye, CheckCircle, XCircle,
  Clock, AlertCircle, DollarSign, User, Bitcoin,
  Calendar, Download, FileText, Image, ExternalLink,
  ChevronLeft, ChevronRight, Plus, Minus,
  Shield, Users, TrendingUp, TrendingDown, Wallet
} from 'lucide-react';
import { AdminWalletAPI, AdminAPIUtils, AdminTokenService } from '../services/api';

const DepositManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [filters, setFilters] = useState({
    status: 'pending',
    search: '',
    user_id: ''
  });
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [actionType, setActionType] = useState('');

  useEffect(() => {
    fetchStats();
    fetchDeposits();
  }, [pagination.page, filters.status]);

  const fetchStats = async () => {
    try {
      const data = await AdminWalletAPI.getDepositProofStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchDeposits = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.user_id) {
        params.user_id = filters.user_id;
      }

      const response = await AdminWalletAPI.getDepositProofs(params);
      setDeposits(response.deposits || []);
      setPagination({
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.pages || 1
      });
    } catch (err) {
      console.error('Failed to fetch deposits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessDeposit = async (proofId, action) => {
    if (!proofId) return;
    
    setActionType(action);
    setSelectedDeposit(deposits.find(d => d.id === proofId));
    setShowModal(true);
  };

  const confirmProcess = async () => {
    if (!selectedDeposit) return;
    
    try {
      setProcessing(true);
      await AdminWalletAPI.processDepositProof(
        selectedDeposit.id,
        actionType,
        notes
      );
      
      // Refresh data
      await fetchDeposits();
      await fetchStats();
      
      setShowModal(false);
      setNotes('');
      setSelectedDeposit(null);
      setActionType('');
      
      alert(`Deposit ${actionType === 'approve' ? 'approved' : 'rejected'} successfully!`);
    } catch (err) {
      console.error('Failed to process deposit:', err);
      alert('Failed to process deposit. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { 
        bg: 'bg-yellow-500/10', 
        text: 'text-yellow-400', 
        border: 'border-yellow-500/20',
        icon: <Clock className="w-3 h-3" />,
        label: 'Pending'
      },
      completed: { 
        bg: 'bg-emerald-500/10', 
        text: 'text-emerald-400', 
        border: 'border-emerald-500/20',
        icon: <CheckCircle className="w-3 h-3" />,
        label: 'Completed'
      },
      rejected: { 
        bg: 'bg-rose-500/10', 
        text: 'text-rose-400', 
        border: 'border-rose-500/20',
        icon: <XCircle className="w-3 h-3" />,
        label: 'Rejected'
      }
    };
    
    const configItem = config[status] || config.pending;
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {configItem.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statCards = [
    {
      title: 'Pending Deposits',
      value: stats?.pending?.count || 0,
      amount: AdminAPIUtils.formatBTC(stats?.pending?.amount || 0) + ' BTC',
      icon: <Clock className="w-5 h-5" />,
      color: 'from-yellow-500 to-amber-600',
      trend: 'waiting'
    },
    {
      title: 'Approved Deposits',
      value: stats?.completed?.count || 0,
      amount: AdminAPIUtils.formatBTC(stats?.completed?.amount || 0) + ' BTC',
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-600',
      trend: 'up'
    },
    {
      title: 'Rejected Deposits',
      value: stats?.rejected?.count || 0,
      amount: AdminAPIUtils.formatBTC(stats?.rejected?.amount || 0) + ' BTC',
      icon: <XCircle className="w-5 h-5" />,
      color: 'from-rose-500 to-red-600',
      trend: 'down'
    },
    {
      title: 'Total Volume',
      value: stats?.total?.count || 0,
      amount: AdminAPIUtils.formatBTC(stats?.total?.amount || 0) + ' BTC',
      icon: <DollarSign className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-600',
      trend: 'up'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            💰 Deposit Management
          </h1>
          <p className="text-charcoal-gray mt-1">Review and process user deposit proofs</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchDeposits}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">+</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-emerald-400 font-medium">{card.amount}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-5 h-5" />
              <input
                type="text"
                placeholder="Search by username, email, or TX hash..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && fetchDeposits()}
                className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all duration-300"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3">
              <Filter className="text-charcoal-gray w-5 h-5" />
              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, status: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
                <option value="all">All Status</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3">
              <User className="text-charcoal-gray w-5 h-5" />
              <input
                type="text"
                placeholder="User ID (optional)"
                value={filters.user_id}
                onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && fetchDeposits()}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all duration-300"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4 pt-4 border-t border-gray-800">
          <button
            onClick={() => {
              setFilters({ status: 'pending', search: '', user_id: '' });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm text-charcoal-gray hover:text-white transition-colors hover:bg-gray-800/50 px-3 py-1.5 rounded-lg"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Deposits Table */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
              <p className="text-charcoal-gray">Loading deposits...</p>
            </div>
          </div>
        ) : deposits.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No deposits found</h3>
            <p className="text-charcoal-gray mb-6">Try adjusting your filters</p>
            <button
              onClick={() => {
                setFilters({ status: 'pending', search: '', user_id: '' });
                fetchDeposits();
              }}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        User
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Amount
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Bitcoin className="w-4 h-4" />
                        TX Hash
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Submitted
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      Proof
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {deposits.map((deposit) => (
                    <tr 
                      key={deposit.id} 
                      className="hover:bg-gray-800/30 transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                            {deposit.username?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">{deposit.username}</div>
                            <div className="text-xs text-charcoal-gray truncate max-w-[140px]">{deposit.email}</div>
                            <div className="text-xs text-gray-500">ID: {deposit.user_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xl font-bold text-emerald-400">
                          {AdminAPIUtils.formatBTC(deposit.amount)} BTC
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {deposit.tx_hash ? (
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-800 px-2 py-1 rounded font-mono text-gray-300">
                              {deposit.tx_hash.substring(0, 16)}...
                            </code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(deposit.tx_hash);
                                alert('TX hash copied!');
                              }}
                              className="text-charcoal-gray hover:text-white transition-colors"
                            >
                              <FileText className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-charcoal-gray">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{formatDate(deposit.created_at)}</div>
                        {deposit.processed_at && (
                          <div className="text-xs text-charcoal-gray">
                            Processed: {formatDate(deposit.processed_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(deposit.status)}
                      </td>
                      <td className="px-6 py-4">
                        {deposit.screenshot_path ? (
                          <a
                            href={`https://api.diceace21.com/${deposit.screenshot_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-colors text-xs"
                          >
                            <Image className="w-3 h-3" />
                            View
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-xs text-charcoal-gray">No proof</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {deposit.status === 'pending' ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleProcessDeposit(deposit.id, 'approve')}
                              className="p-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300 rounded-lg transition-all duration-300 hover:scale-110"
                              title="Approve Deposit"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleProcessDeposit(deposit.id, 'reject')}
                              className="p-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 hover:text-rose-300 rounded-lg transition-all duration-300 hover:scale-110"
                              title="Reject Deposit"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => navigate(`/users/${deposit.user_id}`)}
                            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg text-xs transition-colors"
                          >
                            <Eye className="w-4 h-4 inline mr-1" />
                            View User
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-charcoal-gray">
                    Page <span className="font-medium text-white">{pagination.page}</span> of{' '}
                    <span className="font-medium text-white">{pagination.totalPages}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === 1
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex items-center space-x-1">
                      {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                            className={`px-4 py-2.5 min-w-[40px] rounded-xl transition-all duration-300 ${
                              pagination.page === pageNum
                                ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                                : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                        <>
                          <span className="px-2 text-charcoal-gray">...</span>
                          <button
                            onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
                            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl hover:scale-105 transition-all duration-300"
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === pagination.totalPages
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Process Modal */}
      {showModal && selectedDeposit && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-night-blue border border-gray-800 rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">
                {actionType === 'approve' ? '✅ Approve Deposit' : '❌ Reject Deposit'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setNotes('');
                  setSelectedDeposit(null);
                  setActionType('');
                }}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-800/30 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-charcoal-gray">User</span>
                  <span className="text-white font-medium">{selectedDeposit.username}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-charcoal-gray">Amount</span>
                  <span className="text-emerald-400 font-bold">
                    {AdminAPIUtils.formatBTC(selectedDeposit.amount)} BTC
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-charcoal-gray">Submitted</span>
                  <span className="text-white text-sm">{formatDate(selectedDeposit.created_at)}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {actionType === 'approve' ? 'Approval Notes (optional)' : 'Rejection Reason *'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={actionType === 'approve' ? 'Add any notes about this approval...' : 'Explain why this deposit is being rejected...'}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors resize-none"
                  rows="4"
                  required={actionType === 'reject'}
                />
                {actionType === 'reject' && !notes && (
                  <p className="text-rose-400 text-sm mt-1">Rejection reason is required</p>
                )}
              </div>

              {selectedDeposit.screenshot_path && (
                <div>
                  <a
                    href={`https://api.diceace21.com/${selectedDeposit.screenshot_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors text-sm"
                  >
                    <Image className="w-4 h-4" />
                    View Proof of Payment
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setNotes('');
                  setSelectedDeposit(null);
                  setActionType('');
                }}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmProcess}
                disabled={processing || (actionType === 'reject' && !notes)}
                className={`flex-1 px-4 py-2.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                  actionType === 'approve'
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white'
                    : 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    {actionType === 'approve' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    {actionType === 'approve' ? 'Approve' : 'Reject'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositManagement;